import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

// Define colors used in SimulationOverlay (ensure these match or map them)
const COLOR_WATER = new THREE.Color("rgba(65, 105, 225, 0.9)");
const COLOR_BURNING = new THREE.Color("rgba(255, 0, 0, 0.9)");
const COLOR_SMOLDERING = new THREE.Color("rgba(170, 80, 0, 0.8)");
const COLOR_INFRASTRUCTURE = new THREE.Color("rgba(160, 160, 160, 0.9)"); // Representative grey
const COLOR_TREE = new THREE.Color("rgba(0, 128, 0, 0.85)"); // Representative green
const COLOR_RETARDANT = new THREE.Color("rgba(255, 105, 180, 0.6)");
const COLOR_DIGGING = new THREE.Color("rgba(139, 69, 19, 0.7)");
const COLOR_EMPTY = new THREE.Color("rgba(40, 40, 40, 1)"); // Dark grey for empty/burnt
const COLOR_INFRASTRUCTURE_BURNT = new THREE.Color("rgba(139, 0, 0, 0.8)");
const COLOR_DEFAULT = new THREE.Color("rgba(60, 60, 60, 1)"); // Fallback
// Station colors matching 2D DroneGrid.js
const STATION_COLOR_3D_INACTIVE = new THREE.Color("rgb(0, 180, 255)"); // Standard blue
const STATION_COLOR_3D_ACTIVE = new THREE.Color("rgb(255, 255, 0)"); // Bright Yellow
const STATION_FLASH_PERIOD_MS_3D = 500; // Match 2D flash speed (was 1000)

// Cell states from SimulationOverlay
const STATE_EMPTY = 0;
const STATE_TREE = 1;
const STATE_BURNING = 2;
const STATE_WATER = 3;
const STATE_INFRASTRUCTURE = 4;
const STATE_SMOLDERING = 5;
const STATE_INFRASTRUCTURE_BURNT = 6;
const STATE_RETARDANT = 7;
const STATE_DIGGING = 8;

const SimulationView3D = ({
  gridState,
  elevationGrid,
  gridDimensions,
  persistentToolMarkers, // { "r,c": toolType }
  droneStations, // Array of { row, col }
  activeMissions, // Array of mission objects { origin: { row, col }, ... }
  zIndexValue = 5, // Default zIndex if needed for positioning
  currentSimTime, // Date object for current simulation time
  weather, // { speed (mph), direction (deg, FROM) } from the weather timeline
}) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const terrainMeshRef = useRef(null);
  const structureGroupRef = useRef(null);
  const stationGroupRef = useRef(null); // Group for stations
  const windArrowGroupRef = useRef(null); // Group for wind arrows
  const animationFrameIdRef = useRef(null);

  const [isInitialized, setIsInitialized] = useState(false);
  const [windData, setWindData] = useState(null); // State for parsed wind data

  // Function to get color based on state
  const getColorForState = (state, r, c, markers) => {
    const key = `${r},${c}`;
    // Prioritize active effects over base state for color
    if (
      state === STATE_RETARDANT ||
      (markers[key] === "RETARDANT_DROP" && state !== STATE_WATER)
    )
      return COLOR_RETARDANT;
    if (
      state === STATE_DIGGING ||
      (markers[key] === "DIG_BREAK" && state !== STATE_WATER)
    )
      return COLOR_DIGGING;

    switch (state) {
      case STATE_WATER:
        return COLOR_WATER;
      case STATE_BURNING:
        return COLOR_BURNING;
      case STATE_SMOLDERING:
        return COLOR_SMOLDERING;
      case STATE_INFRASTRUCTURE:
        return COLOR_INFRASTRUCTURE;
      case STATE_TREE:
        return COLOR_TREE;
      case STATE_INFRASTRUCTURE_BURNT:
        return COLOR_INFRASTRUCTURE_BURNT;
      case STATE_EMPTY:
        return COLOR_EMPTY;
      default:
        return COLOR_DEFAULT; // For null or unexpected states
    }
  };

  // Initialize Three.js Scene
  useEffect(() => {
    if (
      !mountRef.current ||
      isInitialized ||
      !gridDimensions.rows ||
      !gridDimensions.cols
    )
      return;

    const { rows, cols } = gridDimensions;
    const currentMount = mountRef.current;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a1d);
    sceneRef.current = scene;

    // Camera
    const aspect = currentMount.clientWidth / currentMount.clientHeight;
    const camera = new THREE.PerspectiveCamera(50, aspect, 1, 3000);
    const gridWidth = cols;
    const gridHeight = rows;
    camera.position.set(
      gridWidth / 2,
      Math.max(gridWidth, gridHeight) * 1.5,
      gridHeight * 1.1
    );
    camera.lookAt(gridWidth / 2, 0, gridHeight / 2);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    currentMount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.6);
    directionalLight1.position.set(100, 150, 100);
    scene.add(directionalLight1);
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-100, 100, -50);
    scene.add(directionalLight2);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(gridWidth / 2, 0, gridHeight / 2);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.minDistance = 10;
    controls.maxDistance = 1500;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;
    controls.update();
    controlsRef.current = controls;

    // Structure Group
    structureGroupRef.current = new THREE.Group();
    scene.add(structureGroupRef.current);

    // Station Group
    stationGroupRef.current = new THREE.Group();
    scene.add(stationGroupRef.current); // Add station group to the scene

    // Wind Arrow Group
    windArrowGroupRef.current = new THREE.Group();
    scene.add(windArrowGroupRef.current); // Add wind arrow group to the scene

    // Terrain Geometry & Material
    const geometry = new THREE.PlaneGeometry(cols, rows, cols - 1, rows - 1);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      metalness: 0.1,
      roughness: 0.8,
    });
    const terrainMesh = new THREE.Mesh(geometry, material);
    terrainMesh.position.set(cols / 2 - 0.5, 0, rows / 2 - 0.5);
    scene.add(terrainMesh);
    terrainMeshRef.current = terrainMesh;

    // Animation Loop - Modified to include station flashing
    const clock = new THREE.Clock(); // Use clock for smooth animation timing
    const animate = () => {
      animationFrameIdRef.current = requestAnimationFrame(animate);
      controls.update();

      // Station Flashing Logic - Mimic 2D behavior
      if (stationGroupRef.current) {
        const elapsedTimeMs = clock.getElapsedTime() * 1000;
        const shouldBeActiveColor =
          Math.floor(elapsedTimeMs / STATION_FLASH_PERIOD_MS_3D) % 2 === 0;

        stationGroupRef.current.children.forEach((stationMesh) => {
          if (stationMesh.userData.isActive) {
            // Flash between yellow and blue if active
            stationMesh.material.color.copy(
              shouldBeActiveColor
                ? STATION_COLOR_3D_ACTIVE
                : STATION_COLOR_3D_INACTIVE
            );
            stationMesh.material.emissive.setHex(0x000000); // No separate emission needed
          } else {
            // Set to inactive blue and ensure no emission if not active
            stationMesh.material.color.copy(STATION_COLOR_3D_INACTIVE);
            stationMesh.material.emissive.setHex(0x000000);
          }
        });
      }

      renderer.render(scene, camera);
    };
    animate();

    // Resize listener
    const handleResize = () => {
      if (!currentMount || !renderer || !camera) return;
      const width = currentMount.clientWidth;
      const height = currentMount.clientHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    setIsInitialized(true);
    console.log("Three.js scene initialized");

    // Cleanup
    return () => {
      console.log("Cleaning up Three.js scene...");
      window.removeEventListener("resize", handleResize);
      if (animationFrameIdRef.current)
        cancelAnimationFrame(animationFrameIdRef.current);
      controlsRef.current?.dispose();

      sceneRef.current?.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (
          rendererRef.current.domElement &&
          currentMount?.contains(rendererRef.current.domElement)
        ) {
          try {
            currentMount.removeChild(rendererRef.current.domElement);
          } catch (e) {
            console.warn(
              "Error removing renderer DOM element during cleanup:",
              e
            );
          }
        }
      }

      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      controlsRef.current = null;
      terrainMeshRef.current = null;
      structureGroupRef.current = null;
      stationGroupRef.current = null; // Clear station group ref
      windArrowGroupRef.current = null; // Clear wind arrow group ref

      setIsInitialized(false);
      console.log("Three.js scene cleaned up");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridDimensions.rows, gridDimensions.cols]);

  // Wind data comes directly from the weather timeline via props
  useEffect(() => {
    if (!weather || !(weather.speed > 0)) {
      setWindData(null);
      return;
    }
    const newWindData = {
      direction: Math.round(weather.direction ?? 0),
      speed: Math.round(weather.speed),
      variable: false,
    };
    if (
      !windData ||
      windData.direction !== newWindData.direction ||
      windData.speed !== newWindData.speed
    ) {
      setWindData(newWindData);
    }
  }, [weather, windData]);

  // Update Terrain and Structures when props change
  useEffect(() => {
    if (
      !isInitialized ||
      !gridState ||
      !elevationGrid ||
      !terrainMeshRef.current ||
      !structureGroupRef.current ||
      !gridDimensions.rows
    )
      return;

    const terrainGeometry = terrainMeshRef.current.geometry;
    const positionAttribute = terrainGeometry.getAttribute("position");
    let colorAttribute = terrainGeometry.getAttribute("color");
    if (!colorAttribute || colorAttribute.count !== positionAttribute.count) {
      colorAttribute = new THREE.BufferAttribute(
        new Float32Array(positionAttribute.count * 3),
        3
      );
      terrainGeometry.setAttribute("color", colorAttribute);
    }

    const { rows, cols } = gridDimensions;
    const elevationScale = 0.1;

    const structureGeometry = new THREE.BoxGeometry(0.7, 1.2, 0.7);
    const structureMaterial = new THREE.MeshStandardMaterial({
      color: COLOR_INFRASTRUCTURE,
      roughness: 0.7,
      metalness: 0.2,
    });
    const structureBurntMaterial = new THREE.MeshStandardMaterial({
      color: COLOR_INFRASTRUCTURE_BURNT,
      roughness: 0.8,
      metalness: 0.1,
    });

    const existingStructures = new Map();
    structureGroupRef.current.children.forEach((child) => {
      if (child.userData.gridKey) {
        existingStructures.set(child.userData.gridKey, child);
      }
    });
    const structuresToRemove = new Set(existingStructures.keys());

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const index = r * cols + c;
        if (index >= positionAttribute.count) continue;

        const state = gridState[r]?.[c];
        const elevation = (elevationGrid[r]?.[c] || 0) * elevationScale;
        const gridKey = `${r},${c}`;

        positionAttribute.setY(index, elevation);

        const color = getColorForState(
          state,
          r,
          c,
          persistentToolMarkers || {}
        );
        colorAttribute.setXYZ(index, color.r, color.g, color.b);

        const existingMesh = existingStructures.get(gridKey);
        if (
          state === STATE_INFRASTRUCTURE ||
          state === STATE_INFRASTRUCTURE_BURNT
        ) {
          structuresToRemove.delete(gridKey);
          let structureMesh = existingMesh;

          if (!structureMesh) {
            structureMesh = new THREE.Mesh(
              structureGeometry,
              state === STATE_INFRASTRUCTURE
                ? structureMaterial.clone()
                : structureBurntMaterial.clone()
            );
            structureMesh.userData.gridKey = gridKey;
            structureMesh.position.set(c, elevation + 0.6, r);
            structureGroupRef.current.add(structureMesh);
          } else {
            // Update material and position if it already exists
            structureMesh.material =
              state === STATE_INFRASTRUCTURE
                ? structureMaterial
                : structureBurntMaterial;
            structureMesh.position.y = elevation + 0.6; // Ensure Y position updates with terrain
          }
        } else {
          // If the state is no longer infrastructure, ensure it's removed
          if (existingMesh) {
            structuresToRemove.add(gridKey); // Mark for removal if state changed
          }
        }
      }
    }

    // Remove structures that are no longer infrastructure
    structuresToRemove.forEach((keyToRemove) => {
      const meshToRemove = existingStructures.get(keyToRemove);
      if (meshToRemove) {
        structureGroupRef.current.remove(meshToRemove);
        // Optionally dispose geometry/material if not reused
        // meshToRemove.geometry.dispose();
        // meshToRemove.material.dispose();
      }
    });

    positionAttribute.needsUpdate = true;
    colorAttribute.needsUpdate = true;
    terrainGeometry.computeVertexNormals();
  }, [
    gridState,
    elevationGrid,
    gridDimensions,
    persistentToolMarkers,
    isInitialized,
  ]);

  // Update Drone Stations
  useEffect(() => {
    if (
      !isInitialized ||
      !droneStations ||
      !stationGroupRef.current ||
      !elevationGrid ||
      !gridDimensions.rows
    )
      return;

    const activeStationCoords = new Set(
      (activeMissions || [])
        .filter((m) => m.origin) // Ensure origin exists
        .map((m) => `${m.origin.row},${m.origin.col}`)
    );

    const existingStations = new Map();
    stationGroupRef.current.children.forEach((child) => {
      if (child.userData.gridKey) {
        existingStations.set(child.userData.gridKey, child);
      }
    });
    const stationsToRemove = new Set(existingStations.keys());

    const stationGeometry = new THREE.CylinderGeometry(0.3, 0.3, 2.5, 8); // Shared geometry
    const elevationScale = 0.1;
    const { rows, cols } = gridDimensions;

    droneStations.forEach((station) => {
      const { row, col } = station;
      const gridKey = `${row},${col}`;
      stationsToRemove.delete(gridKey); // Mark this station as present

      // Ensure row/col are within bounds before accessing elevationGrid
      if (row >= 0 && row < rows && col >= 0 && col < cols) {
        const elevation = (elevationGrid[row]?.[col] || 0) * elevationScale;
        const isStationActive = activeStationCoords.has(gridKey);

        let stationMesh = existingStations.get(gridKey);

        if (!stationMesh) {
          // Create new station mesh if it doesn't exist
          const stationMaterial = new THREE.MeshPhongMaterial({
            color: STATION_COLOR_3D_INACTIVE, // Start as inactive color
            shininess: 60,
            emissive: 0x000000,
          });
          stationMesh = new THREE.Mesh(stationGeometry, stationMaterial);
          stationMesh.userData.gridKey = gridKey;
          stationMesh.position.set(col, elevation + 1.25, row);
          stationGroupRef.current.add(stationMesh);
        } else {
          // Update position if it already exists (terrain might change)
          stationMesh.position.y = elevation + 1.25;
        }

        // Update active status in userData for the animation loop
        // If active status changed, update it and reset color/emissive if needed
        if (stationMesh.userData.isActive !== isStationActive) {
          stationMesh.userData.isActive = isStationActive;

          // If no longer active, immediately reset color to inactive
          if (!isStationActive) {
            stationMesh.material.color.copy(STATION_COLOR_3D_INACTIVE);
            stationMesh.material.emissive.setHex(0x000000); // Ensure emission is off
          }
        }
      } else {
        console.warn(
          `Drone station at [${row}, ${col}] is out of grid bounds.`
        );
      }
    });

    // Remove stations that are no longer in the droneStations prop
    stationsToRemove.forEach((keyToRemove) => {
      const meshToRemove = existingStations.get(keyToRemove);
      if (meshToRemove) {
        stationGroupRef.current.remove(meshToRemove);
        if (meshToRemove.material) meshToRemove.material.dispose();
        // Geometry is shared, dispose it only once at cleanup
      }
    });

    // Dispose shared geometry only during cleanup, not here
    // stationGeometry.dispose();
  }, [
    droneStations,
    activeMissions,
    isInitialized,
    elevationGrid,
    gridDimensions,
  ]); // Rerun when stations, missions, or related data changes

  // Update Wind Arrows
  useEffect(() => {
    if (
      !isInitialized ||
      !windData ||
      !windArrowGroupRef.current ||
      !elevationGrid ||
      !gridDimensions.rows ||
      windData.variable
    ) {
      // Clear existing arrows if wind becomes variable or data is missing
      if (windArrowGroupRef.current) {
        while (windArrowGroupRef.current.children.length > 0) {
          const arrow = windArrowGroupRef.current.children[0];
          windArrowGroupRef.current.remove(arrow);
          // ArrowHelper manages its own line/cone geometry, dispose if needed
          if (arrow.line && arrow.line.geometry) arrow.line.geometry.dispose();
          if (arrow.line && arrow.line.material) arrow.line.material.dispose();
          if (arrow.cone && arrow.cone.geometry) arrow.cone.geometry.dispose();
          if (arrow.cone && arrow.cone.material) arrow.cone.material.dispose();
        }
      }
      if (windData?.variable)
        console.log("Skipping wind arrow visualization for variable wind.");
      return;
    }

    // Clear existing arrows before adding new ones
    while (windArrowGroupRef.current.children.length > 0) {
      const arrow = windArrowGroupRef.current.children[0];
      windArrowGroupRef.current.remove(arrow);
      if (arrow.line && arrow.line.geometry) arrow.line.geometry.dispose();
      if (arrow.line && arrow.line.material) arrow.line.material.dispose();
      if (arrow.cone && arrow.cone.geometry) arrow.cone.geometry.dispose();
      if (arrow.cone && arrow.cone.material) arrow.cone.material.dispose();
    }

    const { rows, cols } = gridDimensions;
    const elevationScale = 0.1;
    const arrowHeightAboveTerrain = 8; // How high arrows float
    const arrowGridStep = 15; // Spacing of arrows
    const arrowLengthScale = 0.8; // Scale speed to arrow length
    const minArrowLength = 3; // Minimum length for visibility

    // Arrows point where the wind blows TOWARD (direction is meteorological FROM).
    // Grid mapping: +x = east (col), +z = south (row).
    const towardRad = (((windData.direction + 180) % 360) * Math.PI) / 180;
    const dirX = Math.sin(towardRad);
    const dirZ = -Math.cos(towardRad);
    const windDirectionVector = new THREE.Vector3(dirX, 0, dirZ).normalize();

    const arrowLength = Math.max(
      minArrowLength,
      windData.speed * arrowLengthScale
    );
    const arrowColor = 0xffff00; // Yellow

    for (let r = Math.floor(arrowGridStep / 2); r < rows; r += arrowGridStep) {
      for (
        let c = Math.floor(arrowGridStep / 2);
        c < cols;
        c += arrowGridStep
      ) {
        if (r >= 0 && r < rows && c >= 0 && c < cols) {
          const elevation = (elevationGrid[r]?.[c] || 0) * elevationScale;
          const origin = new THREE.Vector3(
            c,
            elevation + arrowHeightAboveTerrain,
            r
          );

          const arrow = new THREE.ArrowHelper(
            windDirectionVector,
            origin,
            arrowLength,
            arrowColor,
            arrowLength * 0.4, // headLength
            arrowLength * 0.2 // headWidth
          );
          windArrowGroupRef.current.add(arrow);
        }
      }
    }
  }, [isInitialized, windData, elevationGrid, gridDimensions]); // Rerun when wind or terrain changes

  return (
    <div
      ref={mountRef}
      style={{
        width: "100%",
        height: "100%", // Ensure container takes up space
        position: "absolute", // Position like other overlays
        top: 0,
        left: 0,
        zIndex: zIndexValue, // Control stacking order
        overflow: "hidden", // Prevent scrollbars within the 3D view
      }}
    />
  );
};

export default SimulationView3D;
